import UnitPageShell from '../components/shared/UnitPageShell';
import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Alert
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';
import PasswordResetDialog from '../components/PasswordResetDialog2';
import { tokens } from '../theme/tokens';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [alias, setAlias] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, token } = useAuth();

  const getMergedParams = (): URLSearchParams => {
    const merged = new URLSearchParams(location.search);
    const hash = window.location.hash || '';
    const queryIndex = hash.indexOf('?');
    if (queryIndex >= 0) {
      const hashParams = new URLSearchParams(hash.slice(queryIndex + 1));
      hashParams.forEach((value, key) => {
        if (!merged.has(key)) {
          merged.set(key, value);
        }
      });
    }
    return merged;
  };

  useEffect(() => {
    const params = getMergedParams();
    const reason = params.get('reason');
    const oauthToken = params.get('sso_token');
    const oauthRole = params.get('sso_role');
    const oauthRoles = params.get('sso_roles');
    const oauthEmail = params.get('sso_email');
    const oauthIsNew = params.get('sso_is_new');

    if (oauthToken && oauthEmail) {
      (async () => {
        try {
          if (oauthRoles) {
            const parsedRoles = oauthRoles.split(',').map((r) => r.trim()).filter(Boolean);
            if (parsedRoles.length > 0) {
              authService.storeRoles(parsedRoles);
            }
          }

          await login(oauthEmail, '', oauthToken, oauthRole || 'user');
          
          // Zeige Benachrichtigung bei neuem User
          if (oauthIsNew === '1') {
            setSuccess('✅ Account erfolgreich erstellt! Die Admins wurden benachrichtigt. Sie können sich jetzt anmelden.');
          }
          navigate('/', { replace: true });
          return;
        } catch (e: any) {
          setError(e?.error || e?.message || 'OAuth-Login fehlgeschlagen.');
        }
      })();
    }

    if (location.pathname === '/login-error' || reason) {
      setError(reason || 'Ups, es ist ein Fehler unterlaufen. Bitte erneut anmelden. (E-000)');
    }
  }, [location.pathname, location.search, location.hash, login, navigate]);

  useEffect(() => {
    if (token) {
      navigate('/', { replace: true });
    }
  }, [token, navigate]);

  // SSO Login Handler
  const handleSSOLogin = async () => {
    setError(null);
    setSuccess(null);
    setSsoLoading(true);

    const returnTo = `${window.location.origin}/#/`;
    authService.startOAuthLogin(returnTo);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    
    try {
      if (isRegistering) {
        if (!alias.trim()) {
          setError('Alias ist erforderlich.');
          setLoading(false);
          return;
        }
        const res = await authService.register(email, password, alias);
        if ((res as any).error) {
          setError((res as any).error);
        } else {
          setSuccess('Registrierung erfolgreich! Warten Sie auf die Freischaltung durch einen Administrator.');
          setEmail('');
          setPassword('');
          setAlias('');
          setIsRegistering(false);
        }
      } else {
        await login(email, password);
        navigate('/');
      }
    } catch (e: any) {
      setError(e?.error || e?.message || 'Ein Fehler ist aufgetreten.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <UnitPageShell title={isRegistering ? 'Registrierung' : 'Login'}>
      <Container component="main" maxWidth={false} disableGutters sx={{ px: { xs: 2, md: 4 } }}>
      <Paper elevation={3} sx={{ mt: 8, p: 4, mx: 'auto', width: '100%', maxWidth: 460 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src="/assets/se_logo_klein.png"
            alt="SE Logo"
            sx={{ width: 'auto', height: 80, mb: 2.5 }}
          />

          <Typography component="h1" variant="h5">
            {isRegistering ? 'Registrierung' : 'Login'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2, width: '100%' }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="E-Mail Adresse"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            {isRegistering && (
              <TextField
                margin="normal"
                fullWidth
                id="alias"
                label="Alias (optional)"
                name="alias"
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
              />
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Passwort"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || ssoLoading}
              sx={{
                mt: 3,
                mb: 2,
                bgcolor: tokens.colors.primary,
                '&:hover': { bgcolor: tokens.colors.primaryHover }
              }}
            >
              {loading ? 'Bitte warten...' : (isRegistering ? 'Registrieren' : 'Login')}
            </Button>

            {!isRegistering && (
              <Button
                fullWidth
                variant="contained"
                color="primary"
                disabled={loading || ssoLoading}
                onClick={handleSSOLogin}
                sx={{ mb: 2 }}
              >
                {ssoLoading ? 'Authentifiziere...' : 'Mit Portal-Login anmelden'}
              </Button>
            )}

            <Button
              fullWidth
              variant="outlined"
              onClick={() => setIsRegistering(!isRegistering)}
              disabled={loading || ssoLoading}
              sx={{ mb: 2 }}
            >
              {isRegistering ? 'Zum Login' : 'Neue Registrierung'}
            </Button>

            {!isRegistering && (
              <Typography
                variant="body2"
                color="primary"
                align="center"
                sx={{ mt: 1, cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => setResetDialogOpen(true)}
              >
                Passwort vergessen?
              </Typography>
            )}

            {isRegistering && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                Hinweis: Nach der Registrierung muss Ihr Account von einem Administrator aktiviert werden.
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      <PasswordResetDialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
      />
      </Container>
    </UnitPageShell>
  );
};

export default Login;